import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import TitleSubtitle from "../../../../components/common/TitleSubtitle";
import Button from "../../../../components/ui/Button";
import ImageValidationInfo from "../../../../components/common/ImageValidationInfo";
import { useImageValidation, IMAGE_SPECS } from "../../../../hooks/useImageValidation";
import { createProgressionConfig, getProgressionConfig, updateProgressionConfig } from "../../../../api/progression";
import { uploadFileToS3 } from "../../../../api/s3";
import { IMAGE_MAX_SIZE_1_MB, IMAGE_MAX_SIZE_300_KB } from "../../../../utils/image";
import styles from "./DefaultAvatar.module.css";
import stepStyles from "../createStep.module.css";

interface DefaultAvatarProps {
    navigateToNext: () => void;
}

const DefaultAvatar: React.FC<DefaultAvatarProps> = ({ navigateToNext }) => {
    const { trackData, updateTrackData } = useTrackCreation();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const token = localStorage.getItem("token") || "";

    const [progressionConfigId, setProgressionConfigId] = useState<string | null>(null);
    const [defaultProgressionImage, setDefaultProgressionImage] = useState<string>("");
    const [defaultAvatarImage, setDefaultAvatarImage] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [uploadingProgression, setUploadingProgression] = useState<boolean>(false);
    const [uploadingAvatar, setUploadingAvatar] = useState<boolean>(false);
    const [progressionSizeError, setProgressionSizeError] = useState<string | null>(null);
    const [avatarSizeError, setAvatarSizeError] = useState<string | null>(null);

    // Use the image validation hooks - they auto-validate when imageUrl changes
    const progressionValidation = useImageValidation({
        type: "progression",
        imageUrl: defaultProgressionImage,
    });
    const avatarValidation = useImageValidation({
        type: "avatar",
        imageUrl: defaultAvatarImage,
    });

    useEffect(() => {
        const loadProgressionConfig = async () => {
            if (!trackData.trackId) return;

            try {
                const response = await getProgressionConfig({ token, trackId: trackData.trackId });
                if (response?.progressionConfig) {
                    setProgressionConfigId(response.progressionConfig.id);
                    setDefaultProgressionImage(response.progressionConfig.defaultProgressionImage || "");
                    setDefaultAvatarImage(response.progressionConfig.defaultAvatarImage || "");
                    updateTrackData("progressionConfig", {
                        id: response.progressionConfig.id,
                        defaultProgressionImage: response.progressionConfig.defaultProgressionImage,
                        defaultAvatarImage: response.progressionConfig.defaultAvatarImage,
                    });
                }
            } catch (error) {
                console.error("Error loading progression config:", error);
            }
        };

        loadProgressionConfig();
    }, [trackData.trackId, token]);

    const handleProgressionImageUpload = async (file: File) => {
        if (!file) return;

        if (file.size > IMAGE_MAX_SIZE_1_MB) {
            setProgressionSizeError(t("imageExceedsMaxSize", { maxSize: t("imageMaxSize1Mb") }));
            return;
        }
        setProgressionSizeError(null);

        setUploadingProgression(true);

        // Validate image before upload
        await progressionValidation.validateImage(file);

        try {
            const response = await uploadFileToS3({ token, file });
            if (response?.url) {
                setDefaultProgressionImage(response.url);
            }
        } catch (error) {
            console.error("Error uploading progression image:", error);
        } finally {
            setUploadingProgression(false);
        }
    };

    const handleAvatarImageUpload = async (file: File) => {
        if (!file) return;

        if (file.size > IMAGE_MAX_SIZE_300_KB) {
            setAvatarSizeError(t("imageExceedsMaxSize", { maxSize: t("imageMaxSize300kb") }));
            return;
        }
        setAvatarSizeError(null);

        setUploadingAvatar(true);

        // Validate image before upload
        await avatarValidation.validateImage(file);

        try {
            const response = await uploadFileToS3({ token, file });
            if (response?.url) {
                setDefaultAvatarImage(response.url);
            }
        } catch (error) {
            console.error("Error uploading avatar image:", error);
        } finally {
            setUploadingAvatar(false);
        }
    };

    // Dropzone for progression image
    const {
        getRootProps: getProgressionRootProps,
        getInputProps: getProgressionInputProps,
        isDragActive: isProgressionDragActive,
    } = useDropzone({
        onDrop: (acceptedFiles) => {
            if (acceptedFiles.length > 0) {
                handleProgressionImageUpload(acceptedFiles[0]);
            }
        },
        onDropRejected: () => {
            setProgressionSizeError(t("imageExceedsMaxSize", { maxSize: t("imageMaxSize1Mb") }));
        },
        accept: { "image/*": [".svg", ".png", ".jpg", ".jpeg", ".webp"] },
        maxSize: IMAGE_MAX_SIZE_1_MB,
        disabled: uploadingProgression,
        noClick: true,
    });

    // Dropzone for avatar image
    const {
        getRootProps: getAvatarRootProps,
        getInputProps: getAvatarInputProps,
        isDragActive: isAvatarDragActive,
    } = useDropzone({
        onDrop: (acceptedFiles) => {
            if (acceptedFiles.length > 0) {
                handleAvatarImageUpload(acceptedFiles[0]);
            }
        },
        onDropRejected: () => {
            setAvatarSizeError(t("imageExceedsMaxSize", { maxSize: t("imageMaxSize300kb") }));
        },
        accept: { "image/*": [".svg", ".png", ".jpg", ".jpeg", ".webp"] },
        maxSize: IMAGE_MAX_SIZE_300_KB,
        disabled: uploadingAvatar,
        noClick: true,
    });

    const handleSave = async () => {
        if (!trackData.trackId) return;

        setLoading(true);
        try {
            if (progressionConfigId) {
                await updateProgressionConfig({
                    token,
                    progressionConfigId,
                    defaultProgressionImage: defaultProgressionImage || undefined,
                    defaultAvatarImage: defaultAvatarImage || undefined,
                });
            } else {
                const response = await createProgressionConfig({
                    token,
                    trackId: trackData.trackId,
                    defaultProgressionImage: defaultProgressionImage || undefined,
                    defaultAvatarImage: defaultAvatarImage || undefined,
                });
                if (response?.progressionConfig) {
                    setProgressionConfigId(response.progressionConfig.id);
                    updateTrackData("progressionConfig", {
                        id: response.progressionConfig.id,
                        defaultProgressionImage,
                        defaultAvatarImage,
                    });
                }
            }
            navigateToNext();
        } catch (error) {
            console.error("Error saving progression config:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.container}>
            <TitleSubtitle
                title={t("createAndManageBaseAvatar")}
                subtitle={t("baseAvatarSubtitle")}
                layout="entityForm"
            />

            <div className={stepStyles.stepDescription}>
                {/* Section 1: Progression Illustration */}
                <div className={styles.sectionCard}>
                    <h3 className={styles.sectionTitle}>{t("avatarProgressionIllustration")}</h3>
                    <p className={styles.sectionDescription}>{t("avatarProgressionDescription")}</p>

                    <div className={styles.specsContainer}>
                        <span className={styles.specBadge}>
                            {t("avatarRatio")}: {IMAGE_SPECS.progression.ratioLabel}
                        </span>
                        <span className={styles.specBadge}>
                            {t("avatarWeight")}: {t("imageMaxSize1Mb")} max.
                        </span>
                    </div>

                    <div
                        {...getProgressionRootProps()}
                        className={`${styles.uploadArea} ${defaultProgressionImage ? styles.hasImage : ""} ${isProgressionDragActive ? styles.dragActive : ""}`}
                    >
                        <input {...getProgressionInputProps()} />
                        {defaultProgressionImage ? (
                            <>
                                <img
                                    src={defaultProgressionImage}
                                    alt={t("avatarProgressionImage")}
                                    className={styles.previewImage}
                                />
                                <p className={styles.uploadAreaText}>{t("avatarDragAndDropOrClick")}</p>
                            </>
                        ) : (
                            <p className={styles.uploadAreaText}>{t("avatarDragAndDropOrClick")}</p>
                        )}
                    </div>

                    <ImageValidationInfo warnings={progressionValidation.warnings} />
                    {progressionSizeError && <p style={{ color: "red", marginTop: "10px" }}>{progressionSizeError}</p>}

                    <div className={styles.chooseFileButton}>
                        <Button
                            label={t("avatarChooseFile")}
                            onClick={() => {
                                const input = document.createElement("input");
                                input.type = "file";
                                input.accept = "image/*";
                                input.onchange = (e: any) => {
                                    if (e.target.files?.[0]) {
                                        handleProgressionImageUpload(e.target.files[0]);
                                    }
                                };
                                input.click();
                            }}
                            disabled={uploadingProgression}
                            className="btn-outline"
                        />
                    </div>
                </div>

                {/* Section 2: Avatar Miniature */}
                <div className={styles.sectionCard}>
                    <h3 className={styles.sectionTitle}>{t("avatarMiniatureTitle")}</h3>
                    <p className={styles.sectionDescription}>{t("avatarMiniatureDescription")}</p>

                    <div className={styles.specsContainer}>
                        <span className={styles.specBadge}>
                            {t("avatarRatio")}: {IMAGE_SPECS.avatar.ratioLabel}
                        </span>
                        <span className={styles.specBadge}>
                            {t("avatarWeight")}: {t("imageMaxSize300kb")} max.
                        </span>
                    </div>

                    <div
                        {...getAvatarRootProps()}
                        className={`${styles.uploadArea} ${defaultAvatarImage ? styles.hasImage : ""} ${isAvatarDragActive ? styles.dragActive : ""}`}
                    >
                        <input {...getAvatarInputProps()} />
                        {defaultAvatarImage ? (
                            <>
                                <img
                                    src={defaultAvatarImage}
                                    alt={t("avatarMiniatureImage")}
                                    className={styles.previewImage}
                                />
                                <p className={styles.uploadAreaText}>{t("avatarDragAndDropOrClick")}</p>
                            </>
                        ) : (
                            <p className={styles.uploadAreaText}>{t("avatarDragAndDropOrClick")}</p>
                        )}
                    </div>

                    <ImageValidationInfo warnings={avatarValidation.warnings} />
                    {avatarSizeError && <p style={{ color: "red", marginTop: "10px" }}>{avatarSizeError}</p>}

                    <div className={styles.chooseFileButton}>
                        <Button
                            label={t("avatarChooseFile")}
                            onClick={() => {
                                const input = document.createElement("input");
                                input.type = "file";
                                input.accept = "image/*";
                                input.onchange = (e: any) => {
                                    if (e.target.files?.[0]) {
                                        handleAvatarImageUpload(e.target.files[0]);
                                    }
                                };
                                input.click();
                            }}
                            disabled={uploadingAvatar}
                            className="btn-outline"
                        />
                    </div>
                </div>
            </div>

            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    label={loading ? t("saving") : t("continue")}
                    onClick={handleSave}
                    disabled={loading || uploadingProgression || uploadingAvatar}
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                />
            </div>
        </div>
    );
};

export default DefaultAvatar;
